import { useTranslation } from "react-i18next";
import PageWrapper from "../../components/PageWrapper";
import useLanguage from "../../utils/useLanguage";
import { UserAuth } from "../../utils/AuthContext";
import { useContext, useEffect, useRef, useState } from "react";
import CustomButton from "../../components/CustomButton";
import ErrorBox from "../../components/ErrorBox";
import Loader, { LOADER_SIZES } from "../../components/Loader";
import InformationBox from "../../components/InformationBox";
import analyzeIcon from "../../assets/analyzeIcon.png";
import LoaderWithProgress from "../../components/LoaderWithProgress";
import { analyzeInvoiceForBank } from "../../services/bank/banksAnalyzeService";
import invoicePdf from "../ai-organization/files/inovice.pdf";
import bankApplicationPDF from "../ai-organization/files/bank-application.pdf";
import cirtificate from "../ai-organization/files/supplierCertificate.pdf";
import associationPdf from "../ai-organization/files/articleOfAsscosiation.pdf";
import confirm from "../ai-organization/files/confirm.pdf";
import moneyTransactionRequest from "../ai-organization/files/moneyTransactionRequest.pdf";
import declarationAndUndertaking from "../ai-organization/files/declarationAndUndertaking.pdf";
import { useNavigate, useParams } from "react-router-dom";
import PDFViewer from "../../components/PDF";
import { useInvoices } from "../ai-organization/Invoices";
import downloadIcon from "../../assets/download-icon.svg";

const urlToFile = async (url, filename, type = "application/pdf") => {
  const response = await fetch(url);
  const blob = await response.blob();
  return new File([blob], filename, { type });
};

const BankInvoicePage = () => {
  const { t } = useTranslation("general");
  const { currentDirection } = useLanguage();
  const { userData } = useContext(UserAuth);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invoiceDocs, setInvoiceDocs] = useState([]);
  const [analyzeLoading, setAnalyzeLoading] = useState(false);
  const [analyzeError, setAnalyzeError] = useState("");
  const { invoices } = useInvoices();
  const navigate = useNavigate();
  const { id } = useParams();

  const fetchInvoice = async () => {
    setLoading(true);
    try {
      // Fetch files and create object URLs
      const [
        invoiceBlob,
        bankAppPdfBlob,
        certBlob,
        assocBlob,
        confirmBlob,
        moneyTransactionRequestBlob,
        declarationAndUndertakingBlob,
      ] = await Promise.all([
        urlToFile(invoicePdf, "invoice.pdf", "application/pdf"),
        urlToFile(
          bankApplicationPDF,
          "bank-application.pdf",
          "application/pdf"
        ),
        urlToFile(cirtificate, "supplierCertificate.pdf", "application/pdf"),
        urlToFile(
          associationPdf,
          "articlesOfAssociation.pdf",
          "application/pdf"
        ),
        urlToFile(confirm, "file-1.pdf", "application/pdf"),
        urlToFile(moneyTransactionRequest, "file-2.pdf", "application/pdf"),
        urlToFile(
          declarationAndUndertaking,
          "declarationAndUndertaking.pdf",
          "application/pdf"
        ),
      ]);

      // Create object URLs
      const invoiceObjectUrl = URL.createObjectURL(invoiceBlob);
      const certObjectUrl = URL.createObjectURL(certBlob);
      const assocObjectUrl = URL.createObjectURL(assocBlob);
      const bankAppPdfObjectUrl = URL.createObjectURL(bankAppPdfBlob);
      const confirmObjectUrl = URL.createObjectURL(confirmBlob);
      const moneyTransactionRequestObjectUrl = URL.createObjectURL(
        moneyTransactionRequestBlob
      );
      const declarationAndUndertakingObjectUrl = URL.createObjectURL(
        declarationAndUndertakingBlob
      );

      const data = {
        id,
        invoiceNumber: invoices.find((invoice) => invoice.id === id)
          ?.invoiceNumber,
        docs: [
          {
            id: 1,
            name: "invoice.pdf",
            filename: "invoice",
            path: invoiceObjectUrl,
            objectUrl: invoiceObjectUrl,
            size: invoiceBlob.size,
            mime_type: "application/pdf",
            image_type: "invoice",
            invoiceId: null,
            subCompanyId: null,
            bookId: null,
            multiContainersInvoiceId: "b925f0fc-1a95-490d-9624-fc148571f234",
            shippedContainerId: null,
            organizationId: null,
          },
          {
            id: 2,
            name: "bank-application.pdf",
            filename: "financialTransferRequestForm",
            path: bankAppPdfObjectUrl,
            objectUrl: bankAppPdfObjectUrl,
            size: bankAppPdfBlob.size,
            mime_type:
              "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            image_type: "bank application",
            invoiceId: null,
            subCompanyId: null,
            bookId: null,
            multiContainersInvoiceId: "5cac0506-b7d8-4d0b-957f-f5fe028636d1",
            shippedContainerId: null,
            organizationId: null,
          },
          {
            id: 3,
            name: "supplierCertificate.pdf",
            filename: "supplierCertificate",
            path: certObjectUrl,
            objectUrl: certObjectUrl,
            size: certBlob.size,
            mime_type: "application/pdf",
            image_type: "certificate",
            invoiceId: null,
            subCompanyId: null,
            bookId: null,
            multiContainersInvoiceId: "5cac0506-b7d8-4d0b-957f-f5fe028636d1",
            shippedContainerId: null,
            organizationId: null,
          },
          {
            id: 4,
            name: "articlesOfAssociation.pdf",
            filename: "articlesOfAssociation",
            path: assocObjectUrl,
            objectUrl: assocObjectUrl,
            size: assocBlob.size,
            mime_type: "application/pdf",
            image_type: "association",
            invoiceId: null,
            subCompanyId: null,
            bookId: null,
            multiContainersInvoiceId: "5cac0506-b7d8-4d0b-957f-f5fe028636d1",
            shippedContainerId: null,
            organizationId: null,
          },
          {
            id: 5,
            name: "confirm.pdf",
            filename: "confirmDocument",
            path: confirmObjectUrl,
            objectUrl: confirmObjectUrl,
            size: confirmBlob.size,
            mime_type: "application/pdf",
            image_type: "association",
            invoiceId: null,
            subCompanyId: null,
            bookId: null,
            multiContainersInvoiceId: "5cac0506-b7d8-4d0b-957f-f5fe028636d1",
            shippedContainerId: null,
            organizationId: null,
          },
          {
            id: 6,
            name: "moneyTransactionRequest.pdf",
            filename: "moneyTransactionRequest",
            path: moneyTransactionRequestObjectUrl,
            objectUrl: moneyTransactionRequestObjectUrl,
            size: moneyTransactionRequestBlob.size,
            mime_type: "application/pdf",
            image_type: "association",
            invoiceId: null,
            subCompanyId: null,
            bookId: null,
            multiContainersInvoiceId: "5cac0506-b7d8-4d0b-957f-f5fe028636d1",
            shippedContainerId: null,
            organizationId: null,
          },
          {
            id: 7,
            name: "declarationAndUndertaking.pdf",
            filename: "declarationAndUndertaking",
            path: declarationAndUndertakingObjectUrl,
            objectUrl: declarationAndUndertakingObjectUrl,
            size: declarationAndUndertakingBlob.size,
            mime_type: "application/pdf",
            image_type: "association",
            invoiceId: null,
            subCompanyId: null,
            bookId: null,
            multiContainersInvoiceId: "5cac0506-b7d8-4d0b-957f-f5fe028636d1",
            shippedContainerId: null,
            organizationId: null,
          },
        ],
      };

      setInvoiceDocs(data?.docs || []);
      setLoading(false);
    } catch (error) {
      console.log("error fetching invoices", error);
      setError("errorFetchingInvoices");
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyzeInvoice = async () => {
    setAnalyzeLoading(true);
    try {
      // Convert PDFs to File objects
      const invoiceFile = await urlToFile(invoicePdf, "invoice.pdf");
      const bankAppFile = await urlToFile(
        bankApplicationPDF,
        "bank-application.pdf",
        "application/pdf"
      );
      const certFile = await urlToFile(cirtificate, "supplierCertificate.pdf");
      const assocFile = await urlToFile(
        associationPdf,
        "articlesOfAssociation.pdf"
      );

      const formData = new FormData();

      formData.append("invoice", invoiceFile);
      formData.append("bankForm", bankAppFile);

      formData.append("supplierCertificate", certFile);
      formData.append("articlesOfAssociation", assocFile);

      const response = await analyzeInvoiceForBank(formData);

      if (response.status === 200) {
        navigate("/bank-analyze-invoice", {
          state: { data: response.data },
        });
      }
    } catch (error) {
      console.log("error analyzing invoice", error);
      setAnalyzeError("errorAnalyzingInvoice");
    } finally {
      setAnalyzeLoading(false);
    }
  };

  const handleDownloadInvoice = () => {
    invoiceDocs.forEach((doc) => {
      const link = document.createElement("a");
      link.href = doc.path;
      link.download = doc.filename;
      link.click();
    });
  };

  useEffect(() => {
    fetchInvoice();
  }, [pagination.currentPage]);

  const inovice = invoices.find((invoice) => invoice.id === id);

  return (
    <PageWrapper
      style={{ direction: currentDirection }}
      title={t("transactionsPage")}
      withBackButton
    >
      <div className="u-margin--top_500 o-row o-row--gap-size_200">
        <span>{t("transactionNumber")}:</span>
        <span className={`t-bold_semi-bold`}>{inovice.invoiceNumber}</span>
      </div>
      <section className="u-margin--top_700">
        {loading && <Loader size={LOADER_SIZES.MEDIUM} />}
        {error && <ErrorBox>{t(error)}</ErrorBox>}

        {!loading && !error && invoiceDocs.length === 0 && (
          <InformationBox>{t("transactionHasNoDocs")}</InformationBox>
        )}

        {!loading && !error && invoiceDocs.length > 0 && (
          <>
            <div className="o-grid o-grid--auto-columns_xsmall o-grid--gap-size_700">
              <PDFViewer
                path={invoiceDocs[0].path}
                filename={invoiceDocs[0].filename}
              />

              <PDFViewer
                path={invoiceDocs[1].path}
                filename={invoiceDocs[1].filename}
              />

              <PDFViewer
                path={invoiceDocs[2].path}
                filename={invoiceDocs[2].filename}
              />

              <PDFViewer
                path={invoiceDocs[3].path}
                filename={invoiceDocs[3].filename}
              />

              <PDFViewer
                path={invoiceDocs[4].path}
                filename={invoiceDocs[4].filename}
              />

              <PDFViewer
                path={invoiceDocs[5].path}
                filename={invoiceDocs[5].filename}
              />

              <PDFViewer
                path={invoiceDocs[6].path}
                filename={invoiceDocs[6].filename}
              />
            </div>

            {analyzeLoading && <LoaderWithProgress />}

            {analyzeError && <ErrorBox>{t(analyzeError)}</ErrorBox>}
            <div className="o-row o-row--gap-size_300 o-row--horizontal-align_end u-padding--bottom_500 u-padding--top_500">
              <CustomButton
                title={t("downloadFiles")}
                variant="blank"
                onClick={handleDownloadInvoice}
                icon={downloadIcon}
              />

              <CustomButton
                icon={analyzeIcon}
                title={t("analyzeInvoice")}
                variant="blank"
                onClick={handleAnalyzeInvoice}
                disabled={analyzeLoading}
              />
            </div>
          </>
        )}
      </section>
    </PageWrapper>
  );
};

export default BankInvoicePage;
